import CircularProgressRing from '@components/primitives/CircularProgressRing'
import { classMerge } from '@components/utilities/classMerge'
import { useEffect, useRef, useState } from 'react'

const PULSE_THRESHOLD_IN_SECONDS = 10

function defaultTimeFormatter(remainingTime: number): string {
  const minutes = Math.floor(remainingTime / 60)
  const seconds = remainingTime % 60

  return `${String(minutes)}:${String(seconds).padStart(2, '0')}`
}

export function CircularTimer({
  backgroundCircleClassName,
  createdAt,
  durationSeconds,
  isPaused = false,
  progressCircleClassName,
  size = 100,
  strokeWidth = 10,
  textClassName,
  timeFormatter,
}: {
  backgroundCircleClassName?: string
  createdAt?: number
  durationSeconds: number
  isPaused?: boolean
  progressCircleClassName?: string
  size?: number
  strokeWidth?: number
  textClassName?: string
  timeFormatter?: (remainingTime: number) => string
}) {
  // Store the initial duration and creation time
  const initialDurationRef = useRef(durationSeconds)

  useEffect(() => {
    setRemainingTime(calculateInitialRemainingTime())
  }, [createdAt, durationSeconds])

  // Calculate initial remaining time based on createdAt timestamp if provided
  const calculateInitialRemainingTime = () => {
    if (!createdAt) return durationSeconds

    // Calculate how much time has passed since creation
    const elapsed = Math.floor((Date.now() - createdAt) / 1000)

    // Make sure we don't go below zero
    return Math.max(0, durationSeconds - elapsed)
  }

  const [remainingTime, setRemainingTime] = useState(
    calculateInitialRemainingTime(),
  )

  const formattedTime =
    timeFormatter?.(remainingTime) ?? defaultTimeFormatter(remainingTime)

  useEffect(() => {
    if (remainingTime === 0 || isPaused) {
      return
    }

    // Start a new countdown interval
    const interval = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 0) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isPaused, remainingTime])

  return (
    <CircularProgressRing
      backgroundCircleClassName={classMerge(
        'fill-transparent stroke-hope400',
        backgroundCircleClassName,
      )}
      className={
        remainingTime > 0 && remainingTime <= PULSE_THRESHOLD_IN_SECONDS
          ? 'animate-pulse'
          : ''
      }
      goal={initialDurationRef.current} // Use the initial duration as the goal
      progress={Math.max(initialDurationRef.current - remainingTime, 0)} // Guard against negative values
      progressCircleClassName={classMerge(
        'fill-transparent stroke-hope800 duration-1000 ease-linear',
        progressCircleClassName,
      )}
      size={size}
      strokeWidth={strokeWidth}
    >
      <p
        className={classMerge(
          'text-base font-bold leading-[150%] text-neutral900',
          textClassName,
        )}
      >
        {formattedTime}
      </p>
    </CircularProgressRing>
  )
}
